import React from 'react';

const SegmentDateClock = ({ date }) => {
  // Responsive scale based on screen size
  const getScale = () => {
    if (typeof window === 'undefined') return 1;
    const width = window.innerWidth;
    if (width <= 480) return 0.45;  // Mobile - much smaller
    if (width <= 768) return 0.6;   // Tablet - smaller
    return 0.85; // Desktop (perfect as is)
  };

  const [scale, setScale] = React.useState(getScale);

  React.useEffect(() => {
    const handleResize = () => setScale(getScale());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // Helper function to convert a single digit to 7-segment pattern
  const getSegmentPattern = (digit) => {
    const patterns = {
      '0': [1, 1, 1, 1, 1, 1, 0],
      '1': [0, 1, 1, 0, 0, 0, 0],
      '2': [1, 1, 0, 1, 1, 0, 1],
      '3': [1, 1, 1, 1, 0, 0, 1],
      '4': [0, 1, 1, 0, 0, 1, 1],
      '5': [1, 0, 1, 1, 0, 1, 1],
      '6': [1, 0, 1, 1, 1, 1, 1],
      '7': [1, 1, 1, 0, 0, 0, 0],
      '8': [1, 1, 1, 1, 1, 1, 1],
      '9': [1, 1, 1, 1, 0, 1, 1],
      ' ': [0, 0, 0, 0, 0, 0, 0]
    };
    return patterns[digit] || [0, 0, 0, 0, 0, 0, 0];
  };

  // Single digit component
  const SevenSegmentDigit = ({ digit, glowColor = '#00ff00', scale = 1 }) => {
    const segments = getSegmentPattern(digit);
    
    const segmentStyle = (isOn, isHorizontal = false) => ({
      position: 'absolute',
      background: isOn ? glowColor : 'rgba(255, 255, 255, 0.08)',
      boxShadow: isOn ? `0 0 ${4 * scale}px ${glowColor}, 0 0 ${8 * scale}px ${glowColor}` : 'none',
      transition: 'all 0.3s ease',
      ...(isHorizontal ? {
        width: `${24 * scale}px`,
        height: `${4 * scale}px`,
        clipPath: 'polygon(10% 0%, 90% 0%, 100% 50%, 90% 100%, 10% 100%, 0% 50%)'
      } : {
        width: `${4 * scale}px`,
        height: `${22 * scale}px`,
        clipPath: 'polygon(50% 0%, 100% 10%, 100% 90%, 50% 100%, 0% 90%, 0% 10%)'
      })
    });

    return (
      <div style={{
        position: 'relative',
        width: `${34 * scale}px`,
        height: `${56 * scale}px`,
        margin: `0 ${3 * scale}px`,
        display: 'inline-block'
      }}>
        {/* Segment A - top */}
        <div style={{
          ...segmentStyle(segments[0], true),
          top: `${2 * scale}px`,
          left: `${5 * scale}px`
        }} />
        
        {/* Segment B - top right */}
        <div style={{
          ...segmentStyle(segments[1]),
          top: `${6 * scale}px`,
          right: `${2 * scale}px`
        }} />
        
        {/* Segment C - bottom right */}
        <div style={{
          ...segmentStyle(segments[2]),
          bottom: `${6 * scale}px`,
          right: `${2 * scale}px`
        }} />
        
        {/* Segment D - bottom */}
        <div style={{
          ...segmentStyle(segments[3], true),
          bottom: `${2 * scale}px`,
          left: `${5 * scale}px`
        }} />
        
        {/* Segment E - bottom left */}
        <div style={{
          ...segmentStyle(segments[4]),
          bottom: `${6 * scale}px`,
          left: `${2 * scale}px`
        }} />
        
        {/* Segment F - top left */}
        <div style={{
          ...segmentStyle(segments[5]),
          top: `${6 * scale}px`,
          left: `${2 * scale}px`
        }} />
        
        {/* Segment G - middle */}
        <div style={{
          ...segmentStyle(segments[6], true),
          top: '50%',
          left: `${5 * scale}px`,
          transform: 'translateY(-50%)'
        }} />
      </div>
    );
  };

  // Separator component (for dashes)
  const Separator = ({ glowColor = '#00ff00', scale = 1 }) => (
    <div style={{
      margin: `0 ${4 * scale}px`,
      height: `${56 * scale}px`,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      gap: `${3 * scale}px`
    }}>
      <div style={{
        width: `${6 * scale}px`,
        height: `${4 * scale}px`,
        background: glowColor,
        boxShadow: `0 0 ${4 * scale}px ${glowColor}, 0 0 ${8 * scale}px ${glowColor}`
      }} />
      <div style={{
        width: `${6 * scale}px`,
        height: `${4 * scale}px`,
        background: glowColor,
        boxShadow: `0 0 ${4 * scale}px ${glowColor}, 0 0 ${8 * scale}px ${glowColor}`
      }} />
    </div>
  );

  // Format the date
  const formatDate = (date) => {
    if (!date) return "  -  -    ";
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString();
    
    return `${day}-${month}-${year}`;
  };

  const formattedDate = formatDate(date);
  const glowColor = '#00fff2'; // Retro green glow

  const containerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: `${16 * scale}px ${28 * scale}px`,
    background: 'linear-gradient(145deg, #2a2a2a, #1a1a1a, #0d0d0d)',
    borderRadius: `${6 * scale}px`,
    margin: `${20 * scale}px auto`,
    maxWidth: `${360 * scale}px`,
    boxShadow: `
      inset 0 0 ${12 * scale}px rgba(0, 0, 0, 0.9),
      inset 0 ${2 * scale}px ${3 * scale}px rgba(255, 255, 255, 0.1),
      inset 0 ${-2 * scale}px ${3 * scale}px rgba(0, 0, 0, 0.5),
      0 0 ${16 * scale}px rgba(0, 255, 136, 0.2),
      0 ${3 * scale}px ${6 * scale}px rgba(0, 0, 0, 0.6)
    `,
    border: `${2 * scale}px solid transparent`,
    backgroundClip: 'padding-box',
    position: 'relative',
    overflow: 'hidden',
    // Responsive adjustments
    ...(scale <= 0.45 && {
      margin: `${12 * scale}px ${8 * scale}px`,
      maxWidth: 'calc(100vw - 30px)',
      padding: `${12 * scale}px ${20 * scale}px`
    }),
    ...(scale <= 0.6 && scale > 0.45 && {
      margin: `${15 * scale}px ${10 * scale}px`,
      maxWidth: 'calc(100vw - 40px)'
    })
  };

  return (
    <div style={containerStyle}>
      {/* Vintage border effect */}
      <div style={{
        position: 'absolute',
        top: `${-2 * scale}px`,
        left: `${-2 * scale}px`,
        right: `${-2 * scale}px`,
        bottom: `${-2 * scale}px`,
        background: 'linear-gradient(45deg, #4a4a4a, #666, #333, #555, #4a4a4a)',
        borderRadius: `${8 * scale}px`,
        zIndex: -1
      }} />
      
      {/* Inner metallic rim */}
      <div style={{
        position: 'absolute',
        top: `${1.5 * scale}px`,
        left: `${1.5 * scale}px`,
        right: `${1.5 * scale}px`,
        bottom: `${1.5 * scale}px`,
        border: `${1 * scale}px solid rgba(255, 255, 255, 0.3)`,
        borderRadius: `${4 * scale}px`,
        boxShadow: `inset 0 ${1 * scale}px 0 rgba(255, 255, 255, 0.2), inset 0 ${-1 * scale}px 0 rgba(0, 0, 0, 0.3)`
      }} />
      
      {/* Background grid pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          linear-gradient(rgba(0, 255, 136, 0.05) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0, 255, 136, 0.05) 1px, transparent 1px)
        `,
        backgroundSize: `${12 * scale}px ${12 * scale}px`,
        opacity: 0.4
      }} />
      
      <div style={{
        position: 'relative',
        zIndex: 2
      }}>
        {/* Label */}
        <div style={{
          color: glowColor,
          fontSize: `${10 * scale}px`,
          fontFamily: 'monospace',
          textAlign: 'center',
          marginBottom: `${12 * scale}px`,
          textShadow: `0 0 ${6 * scale}px ${glowColor}`,
          letterSpacing: `${1.2 * scale}px`
        }}>
          LAST UPDATED
        </div>
        
        {/* Date display */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {formattedDate.split('').map((char, index) => {
            if (char === '-') {
              return <Separator key={index} glowColor={glowColor} scale={scale} />;
            }
            return <SevenSegmentDigit key={index} digit={char} glowColor={glowColor} scale={scale} />;
          })}
        </div>
      </div>
    </div>
  );
};

export default SegmentDateClock;